import { refreshAccessToken } from './tasService';

/**
 * In-memory token manager for Vista.
 *
 * Mirrors STM-UI's TokenManagerService — the access token is held only in a
 * module-scoped variable (never persisted to localStorage) so it is not
 * accessible to XSS payloads that read storage.
 *
 * Cross-tab logout is signalled via a dedicated localStorage sentinel key
 * (`vista_logged_out`) rather than by storing the token itself.
 */

let accessToken = null;
let refreshPromise = null;
let lastRefreshTime = 0;
let lastRefreshFailed = false;

const listeners = new Set();
const activityListeners = new Set();

const LOGOUT_FLAG = 'vista_logged_out';

const CHANNEL_EVENTS = Object.freeze({
    TOKEN_UPDATE: 'TOKEN_UPDATE',
    TOKEN_CLEAR: 'TOKEN_CLEAR',
    ACTIVITY_PING: 'ACTIVITY_PING',
    TOKEN_REFRESH_FAILED: 'TOKEN_REFRESH_FAILED',
});

const channel =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('vista_token_sync') : null;

if (channel) {
    channel.onmessage = (event) => {
        const { type, access } = event.data;
        if (type === CHANNEL_EVENTS.TOKEN_UPDATE) {
            accessToken = access;
            lastRefreshTime = Date.now();
            listeners.forEach((fn) => fn({ access }));
        } else if (type === CHANNEL_EVENTS.TOKEN_CLEAR) {
            accessToken = null;
            refreshPromise = null;
            listeners.forEach((fn) => fn({ access: null }));
        } else if (type === CHANNEL_EVENTS.TOKEN_REFRESH_FAILED) {
            lastRefreshFailed = true;
        } else if (type === CHANNEL_EVENTS.ACTIVITY_PING) {
            activityListeners.forEach((fn) => fn());
        }
    };
}

class TokenManagerService {
    /**
     * Returns the current in-memory access token (or null).
     *
     * @returns {string|null}
     */
    static getAccessToken() {
        return accessToken;
    }

    /**
     * Stores a new access token in memory and notifies listeners + other tabs.
     *
     * @param {{ access: string }} tokens
     */
    static setTokens({ access }) {
        lastRefreshFailed = false;
        accessToken = access;
        localStorage.removeItem(LOGOUT_FLAG);
        listeners.forEach((fn) => fn({ access }));

        if (channel) {
            channel.postMessage({ type: CHANNEL_EVENTS.TOKEN_UPDATE, access });
        }
    }

    /**
     * Broadcasts an activity ping to other tabs so their session timers reset.
     */
    static broadcastActivity() {
        if (channel) {
            channel.postMessage({ type: CHANNEL_EVENTS.ACTIVITY_PING });
        }
    }

    /**
     * Subscribe to cross-tab activity pings (for session timer sync).
     *
     * @param {Function} listener
     * @returns {Function} unsubscribe
     */
    static onActivity(listener) {
        activityListeners.add(listener);
        return () => activityListeners.delete(listener);
    }

    /**
     * Clears the in-memory token and notifies listeners + other tabs.
     */
    static clearTokens() {
        accessToken = null;
        refreshPromise = null;
        listeners.forEach((fn) => fn({ access: null }));

        if (channel) {
            channel.postMessage({ type: CHANNEL_EVENTS.TOKEN_CLEAR });
        }
    }

    /**
     * Marks this tab as explicitly logged-out so that a stale refresh cookie
     * does not silently re-authenticate the user.
     */
    static markLoggedOut() {
        localStorage.setItem(LOGOUT_FLAG, 'true');
    }

    /**
     * @returns {boolean}
     */
    static isLoggedOut() {
        return localStorage.getItem(LOGOUT_FLAG) === 'true';
    }

    /**
     * Subscribe to token changes.
     *
     * @param {Function} listener
     * @returns {Function} unsubscribe
     */
    static subscribe(listener) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    }

    /**
     * Attempts a silent refresh via TAS POST /refresh (cookie-based).
     *
     * Uses navigator.locks to deduplicate across tabs (mirrors STM-UI).
     * Falls back to a singleton promise for browsers without Web Locks API.
     *
     * On success the new access token is stored in memory.
     * On failure the token is cleared and other tabs are notified.
     *
     * @returns {Promise<string>} The new access token.
     */
    static async refreshAccessToken() {
        if (refreshPromise) {
            return refreshPromise;
        }

        const requestedAt = Date.now();

        const doRefresh = async () => {
            // Another tab already refreshed while we waited for the lock
            if (lastRefreshTime > requestedAt) {
                return accessToken;
            }
            // Another tab's refresh failed — don't pile on
            if (lastRefreshFailed) {
                throw new Error('Refresh failed in another tab');
            }
            try {
                const { accessToken: newAccess } = await refreshAccessToken();
                lastRefreshTime = Date.now();
                TokenManagerService.setTokens({ access: newAccess });
                return newAccess;
            } catch (error) {
                lastRefreshFailed = true;
                TokenManagerService.clearTokens();
                if (channel) {
                    channel.postMessage({ type: CHANNEL_EVENTS.TOKEN_REFRESH_FAILED });
                }
                throw error;
            } finally {
                refreshPromise = null;
            }
        };

        // Use Web Locks API when available (cross-tab dedup); fall back to promise singleton
        if (typeof navigator !== 'undefined' && navigator.locks) {
            refreshPromise = navigator.locks.request('vista_token_refresh', doRefresh);
        } else {
            refreshPromise = doRefresh();
        }

        return refreshPromise;
    }
}

export default TokenManagerService;

